import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import VisibleField from '../../../components/visible-field/visible-field.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import MenuItemAddNewItem from '../../../components/menu-item-add-new-item/menu-item-add-new-item.component';
import NewItemLabelBlock from '../../../components/new-item-label-block/new-item-label-block.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import FormService from '../../../services/form-service';
import StructureService from '../../../services/structure-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import { CONTACT_CREATE, DEAL_COMMON_EDIT } from '../../../constants/entity/permissions';
import {
  DEAL_TEMPLATE_KEY_BROKER, DEAL_TEMPLATE_KEY_DEAL_CONTACT, DEAL_TEMPLATE_KEY_DEAL_STRUCTURE, DEAL_TEMPLATE_KEY_LENDER,
  DEAL_TEMPLATE_KEY_SPONSOR,
} from '../../../constants/entity/deal-template-keys';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../../constants/entity/contact-types';
import { DEAL_STATUS_DRAFT } from '../../../constants/entity/deal-statuses';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../static/images/person-default-image.png';

const DealCommonData: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isNewDeal,
    dealData,
    dealHiddenFieldList,
    dealStatusList,
    contactCompanyList,
    contactPersonList,
    contactPersonInternalList,
    contactTypeList,
    portfolioList,
    onSponsorsChange,
    onBrokersChange,
    onLendersChange,
    onSponsorContactsChange,
    onBrokerContactsChange,
    onLenderContactsChange,
    onNewDealActivityDealContactsChange,
    onOpenNewSponsorForm,
    onOpenNewBrokerForm,
    onOpenNewLenderForm,
    onCloseNewSponsorForm,
    onCloseNewBrokerForm,
    onCloseNewLenderForm,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactPersonInternalSearch,
    onContactPersonInternalSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [isExpanded, setIsExpanded]: [boolean, Function] = useState(true);
  const [isNewSponsorsVisible, setIsNewSponsorsVisible]: [boolean, Function] = useState(false);
  const [isNewBrokersVisible, setIsNewBrokersVisible]: [boolean, Function] = useState(false);
  const [isNewLendersVisible, setIsNewLendersVisible]: [boolean, Function] = useState(false);
  const [newSponsorIndexes, setNewSponsorIndexes]: [number[], Function] = useState([]);
  const [newBrokerIndexes, setNewBrokerIndexes]: [number[], Function] = useState([]);
  const [newLenderIndexes, setNewLenderIndexes]: [number[], Function] = useState([]);
  const [isNewSponsorContactsVisible, setIsNewSponsorContactsVisible]: [boolean, Function] = useState(false);
  const [isNewBrokerContactsVisible, setIsNewBrokerContactsVisible]: [boolean, Function] = useState(false);
  const [isNewLenderContactsVisible, setIsNewLenderContactsVisible]: [boolean, Function] = useState(false);
  const [selectedNewDealActivityDealContacts, setSelectedNewDealActivityDealContacts]: [Contact[] | null, Function]
    = useState<StateValueDefault>(null);
  const [selectedSponsors, setSelectedSponsors]: [Contact[] | null, Function]
    = useState<StateValueDefault>(null);
  const [selectedBrokers, setSelectedBrokers]: [Contact[] | null, Function]
    = useState<StateValueDefault>(null);
  const [selectedLenders, setSelectedLenders]: [Contact[] | null, Function]
    = useState<StateValueDefault>(null);
  const [selectedSponsorContacts, setSelectedSponsorContacts]: [Contact[] | null, Function]
    = useState<StateValueDefault>(null);
  const [selectedBrokerContacts, setSelectedBrokerContacts]: [Contact[] | null, Function]
    = useState<StateValueDefault>(null);
  const [selectedLenderContacts, setSelectedLenderContacts]: [Contact[] | null, Function]
    = useState<StateValueDefault>(null);

  const isMultiplePortfolioGroupView: boolean = useMemo((): boolean => (
    portfolioList && portfolioList.length ? portfolioList
      .map((portfolioItem: Portfolio): number => portfolioItem.group && portfolioItem.group.id)
      .filter((portfolioGroupId: number, portfolioGroupIndex: number, portfolioIdList: number[]): boolean => (
        portfolioIdList.indexOf(portfolioGroupId) === portfolioGroupIndex
      )).length > 1 : false
  ), [portfolioList]);

  const dealCommonEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);

  const formPrefix: string = 'dealData.dealCommonData';

  const menuItemDealStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealStatusList,
  }), [dealStatusList]);

  const menuItemContactPersonActivityDealContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonInternalList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedNewDealActivityDealContacts,
  }), [contactPersonInternalList, selectedNewDealActivityDealContacts]);

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: isMultiplePortfolioGroupView,
    groupKey: 'group',
    groupItemLabelKey: 'name',
    optionItemLabelKey: 'name',
    optionList: portfolioList,
  }), [portfolioList, isMultiplePortfolioGroupView]);

  const menuItemContactCompanySponsorList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedSponsors,
  }), [contactCompanyList, selectedSponsors]);

  const menuItemContactCompanyBrokerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedBrokers,
  }), [contactCompanyList, selectedBrokers]);

  const menuItemContactCompanyLenderList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedLenders,
  }), [contactCompanyList, selectedLenders]);

  const menuItemContactPersonSponsorList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedSponsorContacts,
  }), [contactPersonList, selectedSponsorContacts]);

  const menuItemContactPersonBrokerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedBrokerContacts,
  }), [contactPersonList, selectedBrokerContacts]);

  const menuItemContactPersonLenderList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedLenderContacts,
  }), [contactPersonList, selectedLenderContacts]);

  const menuItemContactTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactTypeList,
  }), [contactTypeList]);

  const onExpandPanel: Function = useCallback((): void => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const openNewSponsorForm: MouseEventHandlerFunction = useCallback((): void => {
    const lastNewSponsorIndex: number = newSponsorIndexes.length ? newSponsorIndexes[newSponsorIndexes.length - 1] : 0;
    const newSponsorIndex: number = lastNewSponsorIndex + 1;

    setNewSponsorIndexes([...newSponsorIndexes, newSponsorIndex]);
    setIsNewSponsorsVisible(true);

    if (onOpenNewSponsorForm) {
      onOpenNewSponsorForm(newSponsorIndex);
    }
  }, [newSponsorIndexes]);

  const closeNewSponsorForm: Function = useCallback((sponsorIndex: number): void => {
    const newSponsorIndexIndex: number = newSponsorIndexes.findIndex((newSponsorIndexItem: number): boolean => (
      newSponsorIndexItem === sponsorIndex
    ));
    const updatedNewSponsorIndexes: number[] = [
      ...StructureService.deleteListItemByIndex(newSponsorIndexes, newSponsorIndexIndex),
    ];

    setNewSponsorIndexes(updatedNewSponsorIndexes);

    if (!updatedNewSponsorIndexes.length) {
      setIsNewSponsorsVisible(false);
    }

    if (onCloseNewSponsorForm) {
      onCloseNewSponsorForm(sponsorIndex);
    }
  }, [newSponsorIndexes]);

  const openNewBrokerForm: MouseEventHandlerFunction = useCallback((): void => {
    const lastNewBrokerIndex: number = newBrokerIndexes.length ? newBrokerIndexes[newBrokerIndexes.length - 1] : 0;
    const newBrokerIndex: number = lastNewBrokerIndex + 1;

    setNewBrokerIndexes([...newBrokerIndexes, newBrokerIndex]);
    setIsNewBrokersVisible(true);

    if (onOpenNewBrokerForm) {
      onOpenNewBrokerForm(newBrokerIndex);
    }
  }, [newBrokerIndexes]);

  const closeNewBrokerForm: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, brokerIndex: number,
  ): void => {
    const newBrokerIndexIndex: number = newBrokerIndexes.findIndex((newBrokerIndexItem: number): boolean => (
      newBrokerIndexItem === brokerIndex
    ));
    const updatedNewBrokerIndexes: number[] = [
      ...(StructureService.deleteListItemByIndex(newBrokerIndexes, newBrokerIndexIndex)),
    ];

    setNewBrokerIndexes(updatedNewBrokerIndexes);

    if (!updatedNewBrokerIndexes.length) {
      setIsNewBrokersVisible(false);
    }

    if (onCloseNewBrokerForm) {
      onCloseNewBrokerForm(brokerIndex);
    }
  }, [newBrokerIndexes]);

  const openNewLenderForm: MouseEventHandlerFunction = useCallback((): void => {
    const lastNewLenderIndex: number = newLenderIndexes.length ? newLenderIndexes[newLenderIndexes.length - 1] : 0;
    const newLenderIndex: number = lastNewLenderIndex + 1;

    setNewLenderIndexes([...newLenderIndexes, newLenderIndex]);
    setIsNewLendersVisible(true);

    if (onOpenNewLenderForm) {
      onOpenNewLenderForm(newLenderIndex);
    }
  }, [newLenderIndexes]);

  const closeNewLenderForm: Function = useCallback((lenderIndex: number): void => {
    const newLenderIndexIndex: number = newLenderIndexes.findIndex((newLenderIndexItem: number): boolean => (
      newLenderIndexItem === lenderIndex
    ));
    const updatedNewLenderIndexes: number[] = [
      ...(StructureService.deleteListItemByIndex(newLenderIndexes, newLenderIndexIndex)),
    ];

    setNewLenderIndexes(updatedNewLenderIndexes);

    if (!updatedNewLenderIndexes.length) {
      setIsNewLendersVisible(false);
    }

    if (onCloseNewLenderForm) {
      onCloseNewLenderForm(lenderIndex);
    }
  }, [newLenderIndexes]);

  const openNewSponsorContactsForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewSponsorContactsVisible(true);
  }, []);

  const closeNewSponsorContactsForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewSponsorContactsVisible(false);
  }, []);

  const openNewBrokerContactsForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewBrokerContactsVisible(true);
  }, []);

  const closeNewBrokerContactsForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewBrokerContactsVisible(false);
  }, []);

  const openNewLenderContactsForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewLenderContactsVisible(true);
  }, []);

  const closeNewLenderContactsForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewLenderContactsVisible(false);
  }, []);

  const newDealActivityDealContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onNewDealActivityDealContactsChange) {
      onNewDealActivityDealContactsChange(contactValues);
    }

    setSelectedNewDealActivityDealContacts(contactValues);
  }, []);

  const sponsorsChange: Function = useCallback((contactValues: Contact): void => {
    if (onSponsorsChange) {
      onSponsorsChange(contactValues);
    }

    setSelectedSponsors(contactValues);
  }, []);

  const brokersChange: Function = useCallback((contactValues: Contact): void => {
    if (onBrokersChange) {
      onBrokersChange(contactValues);
    }

    setSelectedBrokers(contactValues);
  }, []);

  const lendersChange: Function = useCallback((contactValues: Contact): void => {
    if (onLendersChange) {
      onLendersChange(contactValues);
    }

    setSelectedLenders(contactValues);
  }, []);

  const sponsorContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onSponsorContactsChange) {
      onSponsorContactsChange(contactValues);
    }

    setSelectedSponsorContacts(contactValues);
  }, []);

  const brokerContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onBrokerContactsChange) {
      onBrokerContactsChange(contactValues);
    }

    setSelectedBrokerContacts(contactValues);
  }, []);

  const lenderContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onLenderContactsChange) {
      onLenderContactsChange(contactValues);
    }

    setSelectedLenderContacts(contactValues);
  }, []);

  useEffect((): void => {
    if (dealData) {
      const sponsorCompanyList: Contact[] = dealData.sponsors && dealData.sponsors.length
        ? dealData.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
      const sponsorPersonList: Contact[] = dealData.sponsors && dealData.sponsors.length
        ? dealData.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];
      const brokerCompanyList: Contact[] = dealData.brokers && dealData.brokers.length
        ? dealData.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
      const brokerPersonList: Contact[] = dealData.brokers && dealData.brokers.length
        ? dealData.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];
      const lenderCompanyList: Contact[] = dealData.lenders && dealData.lenders.length
        ? dealData.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
      const lenderPersonList: Contact[] = dealData.lenders && dealData.lenders.length
        ? dealData.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];

      if (!selectedNewDealActivityDealContacts) {
        setSelectedNewDealActivityDealContacts(
          dealData.newDealActivity && dealData.newDealActivity.dealContacts
          && dealData.newDealActivity.dealContacts.length ? dealData.newDealActivity.dealContacts : [],
        );
      }

      if (!selectedSponsors) {
        setSelectedSponsors(sponsorCompanyList && sponsorCompanyList.length ? sponsorCompanyList : []);
      }

      if (!selectedBrokers) {
        setSelectedBrokers(brokerCompanyList && brokerCompanyList.length ? brokerCompanyList : []);
      }

      if (!selectedLenders) {
        setSelectedLenders(lenderCompanyList && lenderCompanyList.length ? lenderCompanyList : []);
      }

      if (!selectedSponsorContacts) {
        setSelectedSponsorContacts(sponsorPersonList && sponsorPersonList.length ? sponsorPersonList : []);

        if (sponsorPersonList && sponsorPersonList.length) {
          setIsNewSponsorContactsVisible(true);
        }
      }

      if (!selectedBrokerContacts) {
        setSelectedBrokerContacts(brokerPersonList && brokerPersonList.length ? brokerPersonList : []);

        if (brokerPersonList && brokerPersonList.length) {
          setIsNewBrokerContactsVisible(true);
        }
      }

      if (!selectedLenderContacts) {
        setSelectedLenderContacts(lenderPersonList && lenderPersonList.length ? lenderPersonList : []);

        if (lenderPersonList && lenderPersonList.length) {
          setIsNewLenderContactsVisible(true);
        }
      }
    }
  }, [dealData]);

  return (
    <CheckAccess permissions={dealCommonEditPermissions}>
      <ExpansionGroup
        withOffset
        withExpandedOffset
        isExpanded={isExpanded}
        headerComponent="Deal Common Info"
        onChange={onExpandPanel}
      >
        <Card isContainer isFullWidth>
          <Table>
            <TableBody
              withNoBorders
              withTopAlign
              withSmallPadding
              withTwoEqualColumns={!isSmallScreen}
              withBlockCells={isSmallScreen}
            >
              <TableRow>
                <TableCell>
                  <FormFieldText
                    isOutlined
                    isRequired
                    name={`${formPrefix}.name`}
                    label="Name"
                    placeholder="Name"
                  />
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isRequired
                    isOutlined
                    isDisabled={(dealStatusList && !dealStatusList.length)
                      || (dealData && dealData.status === DEAL_STATUS_DRAFT)}
                    name={`${formPrefix}.status`}
                    label="Status"
                    optionsTotal={dealStatusList && dealStatusList.length}
                  >
                    {menuItemDealStatusList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_DEAL_CONTACT}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    <FormFieldMultiSelect
                      withImage
                      withSearch
                      isOutlined
                      isDisabled={contactPersonInternalList && !contactPersonInternalList.length}
                      blankElementLabel=""
                      nameKey="fullName"
                      name={`${formPrefix}.newDealActivity.dealContactIds`}
                      label="Deal Leads"
                      defaultImage={PersonDefaultImage}
                      optionList={contactPersonInternalList}
                      onValuesChange={newDealActivityDealContactsChange}
                      onSearch={onContactPersonInternalSearch}
                      onClose={onContactPersonInternalSearchClose}
                    >
                      {menuItemContactPersonActivityDealContactList}
                    </FormFieldMultiSelect>
                  </VisibleField>
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isRequired
                    isOutlined
                    isDisabled={portfolioList && !portfolioList.length}
                    name={`${formPrefix}.asset.portfolioId`}
                    label="Portfolio"
                    optionsTotal={portfolioList && portfolioList.length}
                  >
                    {menuItemPortfolioList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_SPONSOR}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    <FormFieldMultiSelect
                      withImage
                      withSearch
                      isOutlined
                      blankElementLabel=""
                      name={`${formPrefix}.sponsorIds`}
                      label="Sponsors"
                      defaultImage={CompanyDefaultImage}
                      onSearch={onContactCompanySearch}
                      onClose={onContactCompanySearchClose}
                      optionList={contactCompanyList}
                      onValuesChange={sponsorsChange}
                      newItemComponent={(
                        <MenuItemAddNewItem
                          label="New Contact"
                          createPermission={CONTACT_CREATE}
                          onClick={openNewSponsorForm}
                        />
                      )}
                    >
                      {menuItemContactCompanySponsorList}
                    </FormFieldMultiSelect>
                    {
                      isNewSponsorsVisible && (
                        newSponsorIndexes.map((
                          newSponsorIndex: number, newSponsorIndexIndex: number,
                        ): ReactElement => {
                          const formKeySponsorIndex: string = FormService.getIndexedFormFieldKey(newSponsorIndex);

                          return (
                            <Fragment key={newSponsorIndex + 1}>
                              <NewItemLabelBlock
                                withNoCloseButton={newSponsorIndex !== newSponsorIndexes.length}
                                label="Sponsor"
                                labelExtra={`New Contact ${newSponsorIndexIndex + 1}`}
                                onCloseClick={(): void => {
                                  closeNewSponsorForm(newSponsorIndex);
                                }}
                              />
                              <PaddedFlexContainer isSmall>
                                <FormFieldSelect
                                  isDisabled
                                  isOutlined
                                  name={`${formPrefix}.sponsors[${formKeySponsorIndex}].type`}
                                  label="Type"
                                  optionsTotal={contactTypeList && contactTypeList.length}
                                >
                                  {menuItemContactTypeList}
                                </FormFieldSelect>
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  isRequired
                                  name={`${formPrefix}.sponsors[${formKeySponsorIndex}].name`}
                                  label="Name"
                                  placeholder="Name"
                                />
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  label="Email"
                                  name={`${formPrefix}.sponsors[${formKeySponsorIndex}].email`}
                                />
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  label="Website"
                                  name={`${formPrefix}.sponsors[${formKeySponsorIndex}].website`}
                                />
                              </PaddedFlexContainer>
                            </Fragment>
                          );
                        })
                      )
                    }
                    {
                      !isNewSponsorContactsVisible && (
                        <FlexContainer>
                          <ActionButtonNewItem
                            withTopOffset
                            withNoHighlight
                            label="Add Sponsor Contacts"
                            onClick={openNewSponsorContactsForm}
                          />
                        </FlexContainer>
                      )
                    }
                    {
                      isNewSponsorContactsVisible && (
                        <Fragment>
                          <NewItemLabelBlock
                            withNoExtraLabel
                            withNoCloseButton={selectedSponsorContacts && !!selectedSponsorContacts.length}
                            label="Sponsor Contacts"
                            onCloseClick={closeNewSponsorContactsForm}
                          />
                          <FormFieldMultiSelect
                            withImage
                            withSearch
                            isOutlined
                            isDisabled={contactPersonList && !contactPersonList.length}
                            blankElementLabel=""
                            nameKey="fullName"
                            name={`${formPrefix}.sponsorContactIds`}
                            label="Sponsor Contacts"
                            defaultImage={PersonDefaultImage}
                            optionList={contactPersonList}
                            onValuesChange={sponsorContactsChange}
                            onSearch={onContactPersonSearch}
                            onClose={onContactPersonSearchClose}
                          >
                            {menuItemContactPersonSponsorList}
                          </FormFieldMultiSelect>
                        </Fragment>
                      )
                    }
                  </VisibleField>
                </TableCell>
                <TableCell>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_BROKER}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    <FormFieldMultiSelect
                      withImage
                      withSearch
                      isOutlined
                      blankElementLabel=""
                      name={`${formPrefix}.brokerIds`}
                      label="Brokers"
                      defaultImage={CompanyDefaultImage}
                      onSearch={onContactCompanySearch}
                      onClose={onContactCompanySearchClose}
                      optionList={contactCompanyList}
                      onValuesChange={brokersChange}
                      newItemComponent={(
                        <MenuItemAddNewItem
                          label="New Contact"
                          createPermission={CONTACT_CREATE}
                          onClick={openNewBrokerForm}
                        />
                      )}
                    >
                      {menuItemContactCompanyBrokerList}
                    </FormFieldMultiSelect>
                    {
                      isNewBrokersVisible && (
                        newBrokerIndexes.map((
                          newBrokerIndex: number, newBrokerIndexIndex: number,
                        ): ReactElement => {
                          const formKeyBrokerIndex: string = FormService.getIndexedFormFieldKey(newBrokerIndex);

                          return (
                            <Fragment key={newBrokerIndex + 1}>
                              <NewItemLabelBlock
                                withNoCloseButton={newBrokerIndex !== newBrokerIndexes.length}
                                label="Broker"
                                labelExtra={`New Contact ${newBrokerIndexIndex + 1}`}
                                onCloseClick={(): void => {
                                  closeNewBrokerForm(newBrokerIndex);
                                }}
                              />
                              <PaddedFlexContainer isSmall>
                                <FormFieldSelect
                                  isDisabled
                                  isOutlined
                                  name={`${formPrefix}.brokers[${formKeyBrokerIndex}].type`}
                                  label="Type"
                                  optionsTotal={contactTypeList && contactTypeList.length}
                                >
                                  {menuItemContactTypeList}
                                </FormFieldSelect>
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  isRequired
                                  name={`${formPrefix}.brokers[${formKeyBrokerIndex}].name`}
                                  label="Name"
                                  placeholder="Name"
                                />
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  label="Email"
                                  name={`${formPrefix}.brokers[${formKeyBrokerIndex}].email`}
                                />
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  label="Website"
                                  name={`${formPrefix}.brokers[${formKeyBrokerIndex}].website`}
                                />
                              </PaddedFlexContainer>
                            </Fragment>
                          );
                        })
                      )
                    }
                    {
                      !isNewBrokerContactsVisible && (
                        <FlexContainer>
                          <ActionButtonNewItem
                            withTopOffset
                            withNoHighlight
                            label="Add Broker Contacts"
                            onClick={openNewBrokerContactsForm}
                          />
                        </FlexContainer>
                      )
                    }
                    {
                      isNewBrokerContactsVisible && (
                        <Fragment>
                          <NewItemLabelBlock
                            withNoExtraLabel
                            withNoCloseButton={selectedBrokerContacts && !!selectedBrokerContacts.length}
                            label="Broker Contacts"
                            onCloseClick={closeNewBrokerContactsForm}
                          />
                          <FormFieldMultiSelect
                            withImage
                            withSearch
                            isOutlined
                            isDisabled={contactPersonList && !contactPersonList.length}
                            blankElementLabel=""
                            nameKey="fullName"
                            name={`${formPrefix}.brokerContactIds`}
                            label="Broker Contacts"
                            optionList={contactPersonList}
                            defaultImage={PersonDefaultImage}
                            onValuesChange={brokerContactsChange}
                            onSearch={onContactPersonSearch}
                            onClose={onContactPersonSearchClose}
                          >
                            {menuItemContactPersonBrokerList}
                          </FormFieldMultiSelect>
                        </Fragment>
                      )
                    }
                  </VisibleField>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_LENDER}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    <FormFieldMultiSelect
                      withImage
                      withSearch
                      isOutlined
                      blankElementLabel=""
                      name={`${formPrefix}.lenderIds`}
                      label="Lenders"
                      defaultImage={CompanyDefaultImage}
                      onSearch={onContactCompanySearch}
                      onClose={onContactCompanySearchClose}
                      optionList={contactCompanyList}
                      onValuesChange={lendersChange}
                      newItemComponent={(
                        <MenuItemAddNewItem
                          label="New Contact"
                          createPermission={CONTACT_CREATE}
                          onClick={openNewLenderForm}
                        />
                      )}
                    >
                      {menuItemContactCompanyLenderList}
                    </FormFieldMultiSelect>
                    {
                      isNewLendersVisible && (
                        newLenderIndexes.map((
                          newLenderIndex: number, newLenderIndexIndex: number,
                        ): ReactElement => {
                          const formKeyLenderIndex: string = FormService.getIndexedFormFieldKey(newLenderIndex);

                          return (
                            <Fragment key={newLenderIndex + 1}>
                              <NewItemLabelBlock
                                withNoCloseButton={newLenderIndex !== newLenderIndexes.length}
                                label="Lender"
                                labelExtra={`New Contact ${newLenderIndexIndex + 1}`}
                                onCloseClick={(): void => {
                                  closeNewLenderForm(newLenderIndex);
                                }}
                              />
                              <PaddedFlexContainer isSmall>
                                <FormFieldSelect
                                  isDisabled
                                  isOutlined
                                  name={`${formPrefix}.lenders[${formKeyLenderIndex}].type`}
                                  label="Type"
                                  optionsTotal={contactTypeList && contactTypeList.length}
                                >
                                  {menuItemContactTypeList}
                                </FormFieldSelect>
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  isRequired
                                  name={`${formPrefix}.lenders[${formKeyLenderIndex}].name`}
                                  label="Name"
                                  placeholder="Name"
                                />
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  label="Email"
                                  name={`${formPrefix}.lenders[${formKeyLenderIndex}].email`}
                                />
                              </PaddedFlexContainer>
                              <PaddedFlexContainer isSmall>
                                <FormFieldText
                                  isOutlined
                                  label="Website"
                                  name={`${formPrefix}.lenders[${formKeyLenderIndex}].website`}
                                />
                              </PaddedFlexContainer>
                            </Fragment>
                          );
                        })
                      )
                    }
                    {
                      !isNewLenderContactsVisible && (
                        <FlexContainer>
                          <ActionButtonNewItem
                            withTopOffset
                            withNoHighlight
                            label="Add Lender Contacts"
                            onClick={openNewLenderContactsForm}
                          />
                        </FlexContainer>
                      )
                    }
                    {
                      isNewLenderContactsVisible && (
                        <Fragment>
                          <NewItemLabelBlock
                            withNoExtraLabel
                            withNoCloseButton={selectedLenderContacts && !!selectedLenderContacts.length}
                            label="Lender Contacts"
                            onCloseClick={closeNewLenderContactsForm}
                          />
                          <FormFieldMultiSelect
                            withImage
                            withSearch
                            isOutlined
                            isDisabled={contactPersonList && !contactPersonList.length}
                            blankElementLabel=""
                            nameKey="fullName"
                            name={`${formPrefix}.lenderContactIds`}
                            label="Lender Contacts"
                            defaultImage={PersonDefaultImage}
                            optionList={contactPersonList}
                            onValuesChange={lenderContactsChange}
                            onSearch={onContactPersonSearch}
                            onClose={onContactPersonSearchClose}
                          >
                            {menuItemContactPersonLenderList}
                          </FormFieldMultiSelect>
                        </Fragment>
                      )
                    }
                  </VisibleField>
                </TableCell>
                <TableCell>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_DEAL_STRUCTURE}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    <FormFieldText isOutlined name={`${formPrefix}.dealStructure`} label="Deal Structure" />
                  </VisibleField>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      </ExpansionGroup>
    </CheckAccess>
  );
});

export default DealCommonData;
